/**
 * Event dates are UK calendar days (Europe/London), matching the API.
 * Upcoming list: events on today's UK date or later (no grace period after event day).
 * Booking ended state: end of the event day (isPastEventDateEnd).
 */

const UK = "Europe/London";

/** Calendar day from API (YYYY-MM-DD prefix or UTC date from ISO). */
export function parseEventDateAsLocalDay(dateInput: string): Date {
  const trimmed = dateInput.trim();
  const isoDate = trimmed.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    const [y, m, d] = isoDate.split("-").map(Number);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return new Date(y, m - 1, d);
    }
  }
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
  }
  return parsed;
}

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const n = new Date(Date.UTC(y, m - 1, d + days));
  return `${n.getUTCFullYear()}-${String(n.getUTCMonth() + 1).padStart(2, "0")}-${String(n.getUTCDate()).padStart(2, "0")}`;
}

function ukYmd(ts: number): string {
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: UK,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(ts));
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? "0";
  return `${g("year")}-${g("month")}-${g("day")}`;
}

function startOfUkDay(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  let lo = Date.UTC(y, m - 1, d) - 36e6;
  let hi = Date.UTC(y, m - 1, d) + 36e6;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (ukYmd(mid) < iso) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** End of event day: 23:59:59.999 UK (same as next UK midnight minus 1ms). */
export function getEndOfEventDayLocal(dateInput: string): Date {
  const iso = dateInput.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return new Date(NaN);
  }
  return new Date(startOfUkDay(addDaysIso(iso, 1)) - 1);
}

export function isPastEventDateEnd(dateInput: string, now: Date = new Date()): boolean {
  return now > getEndOfEventDayLocal(dateInput);
}

/** True when the event falls on today's UK calendar day. */
export function isEventTodayInUk(dateInput: string, now: Date = new Date()): boolean {
  const eventIso = dateInput.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(eventIso)) return false;
  return eventIso === ukYmd(now.getTime());
}

export type EventCardPhase = "expired" | "today" | "upcoming";

export function getEventCardPhase(
  dateInput: string,
  now: Date = new Date(),
): EventCardPhase {
  if (isPastEventDateEnd(dateInput, now)) return "expired";
  if (isEventTodayInUk(dateInput, now)) return "today";
  return "upcoming";
}

/** True when the event belongs on the upcoming list (today or future UK calendar day). */
export function isEventDateStillActive(dateInput: string, now: Date = new Date()): boolean {
  const eventIso = dateInput.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(eventIso)) {
    return !isPastEventDateEnd(dateInput, now);
  }
  return eventIso >= ukYmd(now.getTime());
}

/** True when the event day has ended (for past / attended lists). */
export function isEventPastForAttendedList(
  dateInput: string,
  now: Date = new Date(),
): boolean {
  return isPastEventDateEnd(dateInput, now);
}

/**
 * True when now is within 48 hours before the UK start of the event day,
 * through the end of that event day. Used to reveal attendees on event cards
 * even if the viewer has not booked.
 */
export function isWithin48HoursBeforeEvent(
  dateInput: string,
  now: Date = new Date(),
): boolean {
  const eventIso = dateInput.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(eventIso)) return false;

  const eventStartMs = startOfUkDay(eventIso);
  const windowStartMs = eventStartMs - 48 * 60 * 60 * 1000;
  const eventEndMs = startOfUkDay(addDaysIso(eventIso, 1)) - 1;
  const nowMs = now.getTime();

  return nowMs >= windowStartMs && nowMs <= eventEndMs;
}
